import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

type Annotation = {
  type: string
  label: string
  vertices: {
    x: number
    y: number
  }[]
  image_idx: number
  annotation_id: string
}

type Subrequest = {
  subrequest_id: string
  answers: string[]
  question: string[]
}

type GroundTruth = {
  resolution_id: string
  date_created: string
  date_updated: string
  mark_delete: boolean
  owner_id: string
  program_id: string
  engagement_id: string
  original_annotation_ids: string[]
  annotation_ids: string[]
  image_ids: string[]
  video_ids: string[]
  response: string
  ground_truth: boolean
  training_id: string
  task_id: string
  subrequest_ids: string[]
  answers: string[]
  time_to_close: string
  operator_session_id: string
  annotations: (Annotation & {
    metadata: unknown
    original_annotation_id: string
  })[]
  annotation_type: string
  subrequests: string[]
}

type Engagement = {
  engagement_id: string
  image_ids: string[]
  video_ids: string[]
  annotations: (Annotation & {
    image_id: string
    engagement_level: boolean
  })[]
  images: {
    image_id: string
    location: string
    thumbnail_lg_location: string
    thumbnail_sm_location: string
  }[]
  videos: {
    video_id: string
    location: string
    thumbnail_lg_location: string
    thumbnail_sm_location: string
  }[]
  subrequests: Subrequest[]
  ground_truth?: GroundTruth[]
}

type CreateSessionResponse = {
  trainingSessionId: string
  title: string
  description: string
  operatorId: string
  dateStarted: string
  targetAccuracy: number
  targetResolution: string
  trainingModuleId: string
  media: { location: string }
  program: {
    displayName: string
    code: string
    extendedFeedback: boolean
    icon: {
      location: string
      icon_lg_location: string
      icon_sm_location: string
    }
  }
  engagements: Engagement[]
}

type GetSessionResponse = CreateSessionResponse & {
  resolvedEngagementsCount: number
  remainingEngagementsCount: number
  avgScore: number
  cumulativeScore: number
  avgTTC: number
  cumulativeTTC: number
}

export function useGetTrainingModuleSession(
  missionSpecialistId?: string,
  trainingModuleId?: string,
  disabled?: boolean
) {
  return useQuery({
    queryKey: ['training-module-session', missionSpecialistId, trainingModuleId],
    queryFn: async () => {
      const { data } = await apiClient.get<GetSessionResponse>(
        `/int/v2/mission-specialist/${missionSpecialistId}/training-modules/${trainingModuleId}/sessions`
      )
      return data
    },
    enabled: Boolean(missionSpecialistId && trainingModuleId) && !disabled,
    refetchOnWindowFocus: false,
  })
}

export function useCreateTrainingModuleSession(missionSpecialistId?: string, trainingModuleId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<CreateSessionResponse>(
        `/int/v2/mission-specialist/${missionSpecialistId}/training-modules/${trainingModuleId}/sessions`
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['training-module-session', missionSpecialistId, trainingModuleId],
      })
    },
  })
}
